import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import './site.css';

// Shared chrome for the Anastasia & Co. site: Nav, Footer, CTABand,
// SectionHeader, the reveal engine, and the glyph library — one layout
// system, no one-offs (spec §9). Every public page renders inside <SitePage>.

export const STUDIO_EMAIL = 'hello@anastasia.dev';

// Spec §3.1 + Pricing (added by client request): five links + Start a project.
const NAV_LINKS = [
  { to: '/work', label: 'Work' },
  { to: '/services', label: 'Services' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/film', label: 'Film' },
  { to: '/studio', label: 'Studio' },
  { to: '/contact', label: 'Contact' },
];

/* ---- Reveal engine ----------------------------------------------------------
   One IntersectionObserver per page: everything carrying an .ac-reveal* class
   (or .ac-draw / .ac-steps / .ac-timeline) gets `.in` once it enters the
   viewport. Works everywhere (no CSS view-timeline dependency); a
   MutationObserver catches nodes that mount after data loads. Reduced motion
   lands everything immediately. */
export function useReveal(deps = []) {
  useEffect(() => {
    const sel = '.ac-reveal, .ac-reveal-wipe, .ac-reveal-scale, .ac-reveal-line, .ac-draw, .ac-steps, .ac-timeline';
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    // Elements waiting to land. IO is the primary trigger; a geometry check on
    // scroll/resize is the belt-and-braces fallback for environments where IO
    // never recomputes (embedded/offscreen webviews — seen in the wild).
    const pending = new Set();
    const reveal = (el) => { el.classList.add('in'); pending.delete(el); io?.unobserve(el); };
    const io = 'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => entries.forEach((e) => { if (e.isIntersecting) reveal(e.target); }),
          { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
        )
      : null;
    // "Landed" = the element's top has crossed the reveal line — including
    // elements already scrolled PAST (anchor jumps, fast scrolls): content
    // above you should never sit unrevealed.
    const inView = (el) => {
      const r = el.getBoundingClientRect();
      return r.top < window.innerHeight * 0.94 && (r.width || r.height);
    };
    let tick = 0;
    const check = () => {
      tick = 0;
      pending.forEach((el) => { if (inView(el)) reveal(el); });
    };
    // setTimeout (not rAF): rAF freezes in non-rendering webviews, where this
    // fallback is exactly the path that must keep working.
    const onScroll = () => { if (!tick) tick = setTimeout(check, 100); };
    // Last line of defense: a slow poll (cheap — a handful of rect reads)
    // guarantees reveals even where IO, rAF, AND scroll events are throttled.
    const poll = setInterval(() => { if (pending.size) check(); }, 450);
    const watch = (root) => root.querySelectorAll(sel).forEach((el) => {
      if (el.classList.contains('in') || pending.has(el)) return;
      if (reduce) { el.classList.add('in'); return; }
      pending.add(el);
      io?.observe(el);
    });
    watch(document);
    check();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    const mo = new MutationObserver((muts) => muts.forEach((m) => m.addedNodes.forEach((n) => {
      if (n.nodeType === 1) {
        if (n.matches?.(sel)) watch(n.parentElement || document);
        else watch(n);
        onScroll();
      }
    })));
    mo.observe(document.body, { childList: true, subtree: true });
    return () => {
      io?.disconnect(); mo.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (tick) clearTimeout(tick);
      clearInterval(poll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/* Kinetic type: split a string into per-word rise masks, staggered by --d. */
export function MaskWords({ text, from = 0, step = 0.055 }) {
  return text.split(' ').map((w, i) => (
    <React.Fragment key={i}>
      <span className="ac-mask-word">
        <span style={{ '--d': `${from + i * step}s` }}>{w}</span>
      </span>{' '}
    </React.Fragment>
  ));
}

/* ---- Glyph library — hand-drawn line marks that draw themselves on reveal ---- */
const glyphProps = {
  viewBox: '0 0 48 48', fill: 'none', stroke: 'currentColor',
  strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round',
  'aria-hidden': true,
};
export const GLYPHS = {
  // Identity: a mark being constructed — circle + compass slash
  identity: (props) => (
    <svg {...glyphProps} {...props} className={`ac-draw ${props?.className || ''}`}>
      <circle cx="24" cy="24" r="15" style={{ '--len': 95 }} />
      <path d="M10 38 L38 10" style={{ '--len': 40, '--d': '0.5s' }} />
      <path d="M24 9 L24 4 M24 44 L24 39" style={{ '--len': 12, '--d': '0.8s' }} />
    </svg>
  ),
  // Web: browser frame + layout lines
  web: (props) => (
    <svg {...glyphProps} {...props} className={`ac-draw ${props?.className || ''}`}>
      <rect x="7" y="10" width="34" height="28" style={{ '--len': 124 }} />
      <path d="M7 17 L41 17" style={{ '--len': 34, '--d': '0.45s' }} />
      <path d="M13 24 L27 24 M13 30 L23 30" style={{ '--len': 26, '--d': '0.7s' }} />
      <path d="M32 24 L35 27 L32 30" style={{ '--len': 10, '--d': '0.9s' }} />
    </svg>
  ),
  // Presence: radiating signal arcs
  presence: (props) => (
    <svg {...glyphProps} {...props} className={`ac-draw ${props?.className || ''}`}>
      <circle cx="16" cy="32" r="3" style={{ '--len': 20 }} />
      <path d="M23 25 A11 11 0 0 1 27 32" style={{ '--len': 14, '--d': '0.4s' }} />
      <path d="M28 19 A19 19 0 0 1 35 32" style={{ '--len': 24, '--d': '0.6s' }} />
      <path d="M33 13 A27 27 0 0 1 43 32" style={{ '--len': 34, '--d': '0.8s' }} />
    </svg>
  ),
  // Content: camera aperture
  content: (props) => (
    <svg {...glyphProps} {...props} className={`ac-draw ${props?.className || ''}`}>
      <circle cx="24" cy="24" r="16" style={{ '--len': 101 }} />
      <path d="M24 8 L31 24 M31 24 L16 30 M16 30 L24 8" style={{ '--len': 70, '--d': '0.5s' }} />
      <circle cx="24" cy="24" r="4" style={{ '--len': 26, '--d': '0.9s' }} />
    </svg>
  ),
  // Copy: script lines with a flourish
  copy: (props) => (
    <svg {...glyphProps} {...props} className={`ac-draw ${props?.className || ''}`}>
      <path d="M8 14 Q18 8 26 14 T42 14" style={{ '--len': 40 }} />
      <path d="M8 24 L36 24" style={{ '--len': 28, '--d': '0.4s' }} />
      <path d="M8 32 L30 32" style={{ '--len': 22, '--d': '0.6s' }} />
      <path d="M8 40 L24 40 Q32 40 34 34" style={{ '--len': 28, '--d': '0.8s' }} />
    </svg>
  ),
};

export function Wordmark({ className = '' }) {
  return (
    <span className={className}>
      Anastasia <span className="amp">&amp;</span> Co.
    </span>
  );
}

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the overlay on route change.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <nav className={`ac-nav ${scrolled ? 'scrolled' : ''}`} aria-label="Site">
      <div className="ac-wrap ac-nav-inner">
        <Link className="ac-nav-brand" to="/"><Wordmark /></Link>
        <div className="ac-nav-links">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={({ isActive }) => (isActive ? 'on' : '')}>
              {l.label}
            </NavLink>
          ))}
          <Link className="ac-btn ac-btn-sm" to="/contact"><span>Start a project</span></Link>
        </div>
        <button className="ac-menu-btn" onClick={() => setOpen(true)} aria-label="Open menu">
          Menu
        </button>
      </div>
      {open && (
        <div className="ac-menu" role="dialog" aria-label="Menu">
          <button className="ac-menu-close" onClick={() => setOpen(false)}>Close</button>
          {NAV_LINKS.map((l) => <Link key={l.to} to={l.to}>{l.label}</Link>)}
          <Link to="/contact">Start a project</Link>
        </div>
      )}
    </nav>
  );
}

export function SectionHeader({ kicker, title, wave = true }) {
  return (
    <div className="ac-section-head ac-reveal">
      {kicker && <span className="ac-kicker">{kicker}</span>}
      <h2>{title}</h2>
      {wave && <hr className="ac-wave" aria-hidden="true" />}
    </div>
  );
}

export function Meander() {
  return <hr className="ac-meander" aria-hidden="true" />;
}

// Spec §3.8 — the close band. Animated ink gradient, drifting turquoise glow,
// swimming wave lines along the waterline.
export function CTABand({
  line = 'If the brand, the site, and the content should finally match — that\u2019s the work.',
}) {
  return (
    <section className="ac-cta ac-on-ink">
      <div className="ac-cta-waves" aria-hidden="true" />
      <div className="ac-wrap ac-cta-inner ac-reveal">
        <h2>{line}</h2>
        <Link className="ac-btn" to="/contact"><span>Start a project</span></Link>
      </div>
    </section>
  );
}

// Spec §3.9 — wordmark + tagline · five pillars · contact · legal.
const PILLAR_LINKS = [
  { anchor: 'web', label: 'Website & application design, build, and maintenance' },
  { anchor: 'identity', label: 'Logo design and brand aesthetics' },
  { anchor: 'presence', label: 'Online presence and advertising' },
  { anchor: 'content', label: 'Photography and content creation' },
  { anchor: 'copy', label: 'Brand messaging and copy' },
];

export function Footer() {
  return (
    <footer className="ac-footer ac-on-ink">
      <div className="ac-wrap">
        {/* Oversized wordmark — the TRIONN-style sign-off. Ampersand waves on hover. */}
        <div className="ac-footer-word" aria-hidden="true">
          Anastasia <span className="amp">&amp;</span> Co.
        </div>
        <div className="ac-footer-grid">
          <div className="ac-footer-brand">
            <img src="/ext/media/anastasia-logo.png" alt="Anastasia & Co." loading="lazy" />
            <p>Brand, web, and presence — built as one.</p>
          </div>
          <ul>
            {PILLAR_LINKS.map((p) => (
              <li key={p.anchor}><Link to={`/services#${p.anchor}`}>{p.label}</Link></li>
            ))}
          </ul>
          <ul>
            <li><a href={`mailto:${STUDIO_EMAIL}`}>{STUDIO_EMAIL}</a></li>
            <li><Link to="/approach">How we work</Link></li>
            <li><Link to="/pricing">Pricing</Link></li>
            <li><Link to="/film">Film finance &amp; production</Link></li>
          </ul>
        </div>
        <div className="ac-footer-legal">
          <span>© Anastasia &amp; Co. {new Date().getFullYear()}</span>
          <Link to="/admin">Studio sign-in</Link>
        </div>
      </div>
    </footer>
  );
}

// Page shell: grain + nav + main + (optional CTA) + footer. Scroll restoration
// on route change; anchors (/services#web) still land on their section.
export default function SitePage({ children, cta = true }) {
  const { pathname, hash } = useLocation();
  useReveal([pathname]);
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) { el.scrollIntoView(); return; }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return (
    <div className="ac">
      <div className="ac-grain" aria-hidden="true" />
      <a className="ac-skip" href="#main">Skip to content</a>
      <Nav />
      {/* keyed by pathname: each route change re-runs the 250ms enter rise */}
      <main id="main" key={pathname} className="ac-page-enter">{children}</main>
      {cta && <CTABand />}
      <Footer />
    </div>
  );
}
